import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-public";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";

type LedgerAccountResponse = components["schemas"]["LedgerAccountResponse"];

// The dialog creates all three, so every picker offering it must list all three or a new account vanishes from it.
const SPEND_CLASSIFICATIONS = new Set(["expense", "other_expense", "costs_of_sales"]);

export function isSpendAccount(account: { classification?: string | null }) {
  return SPEND_CLASSIFICATIONS.has(account.classification ?? "");
}
type NewLedgerAccountType = components["schemas"]["NewLedgerAccountType"];

// What each kind means to someone coding spend, not an accountant's taxonomy.
const TYPES: { value: NewLedgerAccountType; label: string; hint: string }[] = [
  { value: "Expense", label: "Expense", hint: "Everyday running costs: meals, travel, software, office supplies." },
  { value: "CostOfSales", label: "Cost of sales", hint: "Costs directly tied to what you sell, such as stock or materials." },
  { value: "OtherExpense", label: "Other expense", hint: "Costs outside normal operations, such as one-off or financing costs." },
];

/** What the account is for: code this expense (default), map a merchant category, a card's default, or just the chart. */
export type CreateLedgerAccountPurpose = "expense" | "mapping" | "card" | "chart";

export interface CreateLedgerAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purpose?: CreateLedgerAccountPurpose;
  /** The connected platform's name, e.g. "QuickBooks". Looked up when not given. */
  platformName?: string | null;
  /** The company in that platform, e.g. "Dolfin Demo". Looked up with the platform when not given. */
  companyName?: string | null;
  /** A category rule covers this expense, so the new account codes this expense only; the description says so. */
  ruleCoversCategory?: boolean;
  onCreated: (account: LedgerAccountResponse) => void;
}

// Created in the person's own platform, which owns the chart, so they never leave to add it and wait for a sync.
export function CreateLedgerAccountDialog({
  open,
  onOpenChange,
  purpose = "expense",
  platformName,
  companyName,
  ruleCoversCategory,
  onCreated,
}: CreateLedgerAccountDialogProps) {
  const api = useDolfinClient();
  // The person should know where the account will live. Pages that already know say so; the rest ask once.
  const [lookedUp, setLookedUp] = useState<{ platform: string | null; company: string | null } | null>(null);
  useEffect(() => {
    if (!open || platformName !== undefined || lookedUp) return;
    api.GET("/v1/accounting/connection", {}).then(({ data }) =>
      setLookedUp({ platform: data?.serviceDisplayName ?? null, company: data?.companyName ?? null }),
    );
  }, [api, open, platformName, lookedUp]);
  const [name, setName] = useState("");
  const [type, setType] = useState<NewLedgerAccountType>("Expense");
  const [code, setCode] = useState("");
  const [codeRequired, setCodeRequired] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setName("");
      setType("Expense");
      setCode("");
      setCodeRequired(false);
    }
  }, [open]);

  const platform = platformName || lookedUp?.platform || "your accounting platform";
  const company = platformName !== undefined ? companyName : lookedUp?.company;

  const outcome =
    purpose === "chart"
      ? { description: "", button: "Create account" }
      : purpose === "mapping"
        ? { description: " Every purchase in this category will then be coded to it.", button: "Create and map" }
        : purpose === "card"
          ? { description: " It then becomes this card's default expense account.", button: "Create and use" }
          : {
              description: ruleCoversCategory
                ? " This expense is then coded to it. Your category rule still codes similar spend, so only this expense changes."
                : " This expense is then coded to it, and similar spend will follow this choice.",
              button: "Create and use",
            };

  async function submit() {
    if (!name.trim()) {
      toast.error("Give the account a name.");
      return;
    }

    setSaving(true);
    const { data, error } = await api.POST("/v1/accounting/ledger-accounts", {
      body: { name: name.trim(), type, code: code.trim() || null },
    });
    setSaving(false);

    if (error || !data) {
      // Xero keys accounts on a code: say so beside the field rather than only in a toast.
      if ((error as { code?: string } | undefined)?.code === "Expense.LedgerAccountCodeRequired") setCodeRequired(true);
      toast.error(apiErrorMessage(error, "Couldn't create the account."));
      return;
    }

    toast.success(`${data.name} added to ${platform}.`);
    onOpenChange(false);
    onCreated(data);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create an account</DialogTitle>
          <DialogDescription>
            Adds a new account to {company ? `${company}'s chart` : "your chart"} in {platform}.{outcome.description}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="new-account-name">Name</Label>
            <Input
              id="new-account-name"
              autoFocus
              maxLength={100}
              placeholder="e.g. Staff welfare"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !saving && submit()}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Kind of account</Label>
            <Select value={type} onValueChange={(v) => setType(v as NewLedgerAccountType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{TYPES.find((t) => t.value === type)?.hint}</p>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="new-account-code">Account code{codeRequired ? "" : " (optional)"}</Label>
            <Input
              id="new-account-code"
              maxLength={20}
              placeholder="e.g. 429"
              className={codeRequired ? "border-amber-500/60" : undefined}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            {codeRequired ? (
              <p className="text-xs text-amber-600">{platform} needs a code for every account.</p>
            ) : null}
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={saving} onClick={submit}>
            {saving ? <Spinner className="size-4" /> : null} {outcome.button}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
